import React, { useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { FormProvider } from 'react-hook-form';
import { DateTime } from 'luxon';
import classnames from 'classnames';
import { useDialogState, useIsBreakpointDown } from 'modules/common/hooks';
import { TimeSignal } from 'graphql/types';
import { NoteContextProvider } from 'modules/notes/NoteContext';
import { useScrollbarStyles } from 'modules/common/styles';
import { useDialogStyles } from '../addNote/AddNoteDialog';
import { useTimeSignalAttributes } from '../addNote/hooks';
import { SplitNoteFooter, SplitNoteBody } from './components';
import { useSplitNoteForm } from './hooks';
import { SplitEntryContextProvider } from './SplitEntriesProvider';
import { CancelSplitDialog } from './components/CancelSplitDialog';

const useStyles = makeStyles((theme) => ({
  title: {
    ...theme.typography.h6,
    color: theme.palette.grey[700],
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 2, 0)
  },
  content: {
    padding: theme.spacing(0),
    overflowX: 'hidden'
  },
  footer: {
    padding: theme.spacing(1, 2.5)
  }
}));

type SplitNoteDialogProps = {
  open: boolean;
  onClose: () => void;
  timeSignal: TimeSignal;
};
export const SplitNoteDialog: React.FC<SplitNoteDialogProps> = ({
  open,
  onClose,
  timeSignal
}: SplitNoteDialogProps) => {
  const classes = useStyles();
  const dialogClasses = useDialogStyles();
  const classesScrollbar = useScrollbarStyles();
  const fullScreen = useIsBreakpointDown('xs');

  const {
    open: cancelNoteOpen,
    closeDialog: cancelCloseDialog,
    openDialog: cancelOpenDialog
  } = useDialogState();

  const { formMethods, ...rest } = useSplitNoteForm(timeSignal);
  const { attributes } = useTimeSignalAttributes(timeSignal.id);

  const onDialogClose = useCallback(
    (_, reason) => {
      if (
        formMethods.formState.isDirty &&
        (reason === 'backdropClick' || reason === 'escapeKeyDown')
      ) {
        cancelOpenDialog();
      } else {
        onClose();
      }
    },
    [cancelOpenDialog, formMethods.formState.isDirty, onClose]
  );
  return (
    //to fix type instantiation is excessively deep and infinite
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <FormProvider {...formMethods}>
      <SplitEntryContextProvider {...rest}>
        <NoteContextProvider date={DateTime.fromISO(timeSignal.date)}>
          <Dialog
            data-testid="split-note-dialog"
            classes={dialogClasses}
            open={open}
            fullScreen={fullScreen}
            disableEnforceFocus
            onClose={onDialogClose}
          >
            <DialogTitle
              className={classes.title}
              data-testid="split-note-dialog-title"
            >
              <FormattedMessage id="note.splitNoteTitle" />
            </DialogTitle>
            <DialogContent
              className={classnames(
                classes.content,
                classesScrollbar.scrollbar
              )}
              data-testid="edit-note-dialog-content"
            >
              <SplitNoteBody note={timeSignal} attributes={attributes} />
            </DialogContent>
            <DialogActions
              className={classes.footer}
              data-testid="split-note-dialog-footer"
            >
              <SplitNoteFooter
                onNoteDiscard={onClose}
                note={timeSignal}
                attributes={attributes}
              />
            </DialogActions>
          </Dialog>
          {cancelNoteOpen && (
            <CancelSplitDialog
              open={cancelNoteOpen}
              closeHandler={cancelCloseDialog}
              cancelSplit={onClose}
            />
          )}
        </NoteContextProvider>
      </SplitEntryContextProvider>
    </FormProvider>
  );
};
